import { getLogger } from '@kit/shared/logger';

import translationOverrides from './i18n.overrides.json';

interface TranslationResource {
  [key: string]: string | TranslationResource;
}

/**
 * Deep merge two translation objects. Override values take precedence over base values.
 * Only plain objects are recursively merged — arrays and primitives are replaced entirely.
 *
 * Example:
 *   base:      { courts: "Courts", calendar: { chooseCourt: "Choose a court" } }
 *   overrides: { courts: "Rooms",  calendar: { chooseCourt: "Choose a room"  } }
 *   result:    { courts: "Rooms",  calendar: { chooseCourt: "Choose a room"  } }
 */
function deepMerge(
  base: TranslationResource,
  overrides: TranslationResource,
): TranslationResource {
  const result = { ...base };

  for (const key of Object.keys(overrides)) {
    const baseVal = result[key];
    const overrideVal = overrides[key]!;

    if (
      typeof baseVal === 'object' &&
      baseVal !== null &&
      typeof overrideVal === 'object' &&
      overrideVal !== null
    ) {
      result[key] = deepMerge(
        baseVal as TranslationResource,
        overrideVal as TranslationResource,
      );
    } else {
      result[key] = overrideVal;
    }
  }

  return result;
}

const overrideNamespaces = new Map(
  Object.entries(translationOverrides.namespaces),
);

/**
 * @name i18nResolver
 * @description Resolve the translation file for the given language and namespace.
 * When a businessType is provided and has override translations, they are
 * deep-merged on top of the base namespace translations. Override files are sparse —
 * they only need to contain keys that differ from the base; all other keys fall back
 * to the base file.
 * @param language - BCP 47 language tag (e.g., 'en', 'ar')
 * @param namespace - Translation namespace (e.g., 'court', 'bookings')
 * @param businessType - Optional business type for loading translation overrides (e.g., 'clinic')
 */
export async function i18nResolver(
  language: string,
  namespace: string,
  businessType?: string | null,
): Promise<TranslationResource> {
  const logger = await getLogger();

  let baseData: TranslationResource = {};

  try {
    // Dynamic import is resolved at build time by webpack/turbopack into
    // separate chunks per locale file. The path must use string literals
    // or template literals with a fixed prefix for the bundler to trace them.
    const baseModule = (await import(
      `../../public/locales/${language}/${namespace}.json`
    )) as TranslationResource & { default?: TranslationResource };

    // JSON dynamic imports may expose the parsed object as `.default` when
    // using ESM interop (esModuleInterop=true + moduleResolution=bundler).
    // Fall back to the module itself if `.default` is absent.
    baseData = baseModule.default ?? baseModule;
  } catch (error) {
    logger.error(
      {
        language,
        namespace,
        error: error instanceof Error ? error.message : String(error),
      },
      `Missing translation file: public/locales/${language}/${namespace}.json`,
    );

    return {};
  }

  if (
    !businessType ||
    !translationOverrides.languages.includes(language) ||
    !overrideNamespaces.get(businessType)?.includes(namespace)
  ) {
    return baseData;
  }

  // Attempt to load business-type-specific overrides (e.g., court.clinic.json).
  // The override file is sparse — it only needs to contain keys that differ from
  // the base. Missing keys in the override fall through to base translations.
  try {
    const overrideModule = (await import(
      `../../public/locales/${language}/${namespace}.${businessType}.json`
    )) as TranslationResource & { default?: TranslationResource };

    const overrideData = overrideModule.default ?? overrideModule;

    return deepMerge(baseData, overrideData);
  } catch (error) {
    logger.error(
      {
        language,
        namespace,
        businessType,
        error: error instanceof Error ? error.message : String(error),
      },
      `Failed to load translation override: public/locales/${language}/${namespace}.${businessType}.json`,
    );

    return baseData;
  }
}
